<!-- #region HTML -->
<template>
  <div class="app">
    <!-- #region Alert Component -->
    <alert ref="alertComponent" :SystemErrorAlert="isSystemError" :codeSent="codeSent"
      @update:SystemErrorAlert="isSystemError = $event" @update:codeSent="codeSent = $event">
    </alert>
    <!-- #endregion -->

    <div class="main-container form-container">
      <!-- #region Navigation -->
      <div class="steps">
        <div class="back-bg" @click="goBack">
          <img class="back" src="@/assets/img/back.svg" alt="戻る" width="100%" />
        </div>
        <div class="right-box">
          <div class="sub-container">
            <div class="title">
              <label>メールアドレス変更</label>
            </div>
            <div class="step-box text-left">
              <label class="step-title sp-hide">ステップ 2/2</label>
              <label class="step-description sp-hide">メールに記載された認証コードを入力し、メールアドレス変更を完了してください。</label>
            </div>
          </div>
          <div class="step-description-box text-left">
            <label class="step-title">ステップ 2/2</label>
            <div class="step-description">メールに記載された認証コードを入力し、メールアドレス変更を完了してください。</div>
          </div>
        </div>
      </div>
      <!-- #endregion Navigation -->

      <!-- #region Form -->
      <form novalidate @submit.prevent="validateUser">
        <md-card class="form-container">

          <!-- #region Verification Code -->
          <div class="md-form">
            <md-field :class="getValidationClass('verificationCode')">
              <md-input class="mb-20" id="verification-code" v-model="form.verificationCode" name="verification-code"
                placeholder="認証コード" :disabled="sending" @input="clearErrorFlag" />
              <span v-if="!$v.form.verificationCode.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.verificationCode.minLength" class="md-error error">認証コードが誤っています。</span>
              <span v-else-if="!$v.form.verificationCode.maxLength" class="md-error error">認証コードが誤っています。</span>
              <span v-else-if="isInvalidVerificationCode === true"
                class="md-error error md-error-verification-code">認証コードが誤っています。</span>
            </md-field>
            <div class="resend-code verify-code">
              <a :class="{ disabled: isResendCode }" @click="resendCode">認証コード再送信</a>
            </div>
          </div>
          <!-- #endregion Verification Code -->

          <!-- #region Password -->
          <div class="md-form">
            <md-field :class="getValidationClass('password')">
              <md-input id="password" v-model="form.password" name="password" :type="passwordInputType"
                placeholder="現在のパスワードの再入力" :disabled="sending" @input="clearErrorFlag" />
              <span class="toggle-password" @click="togglePassword()">
                <img :src="passwordInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワードを表示" width="100%" />
              </span>
              <span v-if="!$v.form.password.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.password.minLength" class="md-error error">8文字以上、64文字以下で入力してください。</span>
              <span v-else-if="!$v.form.password.maxLength" class="md-error error">8文字以上、64文字以下で入力してください。</span>
              <span v-else-if="isInvalidPassword === true"
                class="md-error error md-error-old-password">現在のパスワードが誤っています。</span>
            </md-field>
          </div>
          <!-- #endregion Password -->

          <md-progress-bar v-if="sending" md-mode="indeterminate" />

          <!-- #region Submit Button -->
          <div class="submit-container">
            <md-card-actions>
              <md-button type="submit" class="primary-button" :disabled="sending">確認してメールアドレスを変更</md-button>
            </md-card-actions>
            <md-card-content>
              <label>認証コードが届かない場合は、メーラーの迷惑フォルダーをご確認ください。</label>
            </md-card-content>
          </div>
          <!-- #endregion Submit Button -->

        </md-card>
      </form>
      <!-- #endregion Form -->
    </div>
  </div>
</template>

<!-- #endregion -->

<!-- #region JavaScript -->
<script>
// @ is an alias to /src
import awsconfig from '@/aws-exports'
import apiUrl from '@/const/api-lambda-url.js'
import Alert from '@/layout/Alerts.vue'
import { togglePassword } from '@/utils/base.js'
import { Auth } from 'aws-amplify'
import { validationMixin } from 'vuelidate'
import { maxLength, minLength, required } from 'vuelidate/lib/validators'

let cognito_user
let cognito_before_email
let cognito_standbye_email
const validPassword = async (value) => {
  try {
    const user = await Auth.signIn(cognito_before_email, value)
    cognito_user = user
  } catch (error) {
    if (error.code === 'NotAuthorizedException') {
      return { success: false, errorType: 'invalidPassword' };
    }
    else {
      return { success: false, errorType: 'systemError' };
    }
  }
  return true;
}
const validVerificationCode = async (value) => {
  let verifyResult = ''
  try {
    verifyResult = await Auth.verifyCurrentUserAttributeSubmit('email', value)
  } catch (error) {
    return false
  }
  if (verifyResult === 'SUCCESS') {
    return true
  }
  return false
}
const includesAlphabetsAndNumbers = (value) => {
  const pattern = /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,64}$/i
  return pattern.test(value)
}

export default {
  name: 'VerifyChangeEmail',
  mixins: [validationMixin],
  components: {
    "alert": Alert
  },
  props: {
    username: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    form: {
      oldEmail: null,
      newEmail: null,
      password: null,
      verificationCode: null,
      username: '',
    },
    userSaved: false,
    codeSent: false,
    sending: false,
    isResendCode: false,
    lastUser: null,
    message: '',
    isInvalidVerificationCode: false,
    isInvalidPassword: false,
    isSystemError: false,
    passwordInputType: 'password',
    eyeOpenImg: require('@/assets/img/eye-open.svg'),
    eyeCloseImg: require('@/assets/img/eye-close.svg'),
  }),
  async created() {
    let user = await Auth.currentAuthenticatedUser()
    cognito_standbye_email = user.attributes['custom:standbye_email']
    this.form.newEmail = user.attributes['custom:standbye_email']
    cognito_before_email = user.attributes.email
    this.form.oldEmail = cognito_before_email
  },
  validations: {
    form: {
      password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
      },
      verificationCode: {
        required,
        minLength: minLength(6),
        maxLength: maxLength(6),
      },
    },
  },
  methods: {
    goBack() {
      window.history.back();
    },
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName]
      if (field) {
        if (fieldName == 'verificationCode') {
          if (field.$anyError || this.isInvalidVerificationCode) {
            return { 'md-invalid': true };
          }
        }
        if (fieldName == 'password') {
          if (field.$anyError) {
            this.isInvalidPassword = false
          }
          if (this.isInvalidPassword) {
            return { 'md-invalid': true };
          }
        }
        return {
          'md-invalid': field.$invalid && field.$dirty,
        }
      }
    },
    togglePassword() {
      this.passwordInputType = togglePassword(this.passwordInputType);
    },
    clearForm() {
      this.$v.$reset()
      this.form.password = null
      this.form.verificationCode = null
    },
    clearErrorFlag() {
      this.isInvalidPassword = false
      this.isInvalidVerificationCode = false
      this.isSystemError = false
    },
    async saveUser() {
      this.clearErrorFlag()
      this.sending = true
      try {
        const updateResult = await Auth.updateUserAttributes(cognito_user, {
          email: cognito_standbye_email,
          'custom:validated_email': cognito_standbye_email,
          'custom:before_email': cognito_before_email,
          'custom:standbye_email': '',
          'custom:cdp_post_email': '0',
        })

        await Auth.signOut({ global: true })

        this.$store.commit('clearAuthData')
        this.$emit('callSignout')
        this.$router.push({ name: 'completeChangeEmail' })
        const idToken = cognito_user.signInUserSession.idToken.jwtToken;
        const API_URL = apiUrl.get_api_url(awsconfig.aws_user_pools_id)
        const requestData = {
          username: cognito_user.username,
          newEmail: cognito_standbye_email,
          oldEmail: cognito_before_email,
          idToken: idToken,
        };
        await this.$axios.post(API_URL, requestData)
          .then(function (response) {
            if (response.data.body.isOptInEmail) {
              resIsOptInEmail = response.data.body.isOptInEmail === 1 ? 'true' : 'false'
            }
          }).catch(function (response) {
            this.isSystemError = true
          });
      } catch (error) {
        this.isSystemError = true
        this.sending = false
        return
      }
      this.userSaved = true
      this.sending = false
      this.clearForm()
    },
    async validateUser() {
      this.clearErrorFlag()
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      const passwordValidationResult = await validPassword(this.form.password)
      if (passwordValidationResult.success === false) {
        switch (passwordValidationResult.errorType) {
          case 'invalidPassword':
            this.isInvalidPassword = true
            break;
          default:
            this.isSystemError = true;
        }
        return;
      }
      if (!(await validVerificationCode(this.form.verificationCode))) {
        this.isInvalidVerificationCode = true
        return
      }
      this.saveUser()
    },
    async resendCode() {
      this.clearErrorFlag();
      this.isResendCode = true;
      this.sending = true;

      setTimeout(async () => {
        try {
          let user = await Auth.currentAuthenticatedUser();
          let result = await Auth.updateUserAttributes(user, {
            email: this.form.newEmail,
            'custom:validated_email': cognito_before_email,
            'custom:standbye_email': cognito_standbye_email,
          });
          this.codeSent = true; 
          setTimeout(() => {
            this.codeSent = false;
          }, 5000);

        } catch (error) {
          this.sending = false
          if (error.code == 'AliasExistsException') {
            this.isAliasExists = true
          } else {
            this.isSystemError = true
          }
          return
        }
        this.isResendCode = false;
        this.sending = false;
      }, 1500);
      return false;
    },
  },
}
</script>
<!-- #endregion -->

<!-- #region CSS -->
<style lang="scss" scoped>
/* #region Base CSS */
.submit-container {
  padding-top: 50px;
  padding-bottom: 50px;
}

.md-card .md-card-actions {
  margin: 0 !important;
  padding-top: 0px !important;
  padding-bottom: 30px !important;
}

.md-card .md-card-content {
  padding-top: 0px;
  padding-bottom: 0px;
}

/* #endregion Base CSS */

/*  #region SP */
@media screen and (max-width:768px) {
  .steps {
    margin: 0 auto 30px auto;
  }

  .verify-code {
    margin: 0 auto 30px auto;
  }

  .submit-container {
    padding-top: 30px;
    padding-bottom: 30px;
  }

  .md-card .md-card-actions {
    padding-top: 0px !important;
    padding-bottom: 20px !important;
  }

  .md-card .md-card-content {
    padding-left: var(--card-horizontal-padding);
    padding-right: var(--card-horizontal-padding);
  }
}

/* #endregion SP*/
</style>

<!-- #endregion CSS-->